/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import { call, put } from "redux-saga/effects";
import { AuthApi } from "../../services/api/auth";
import {
  companyData,
  getCountsError,
  getCountsSuccess,
  getHpError,
  getHpSuccess,
  logoutError,
  logoutSuccess,
  mfaPending,
  profileData,
  signInError,
  signInSuccess,
} from "../actions/auth";
import { AuthStorage } from "../../services/storage/auth.storage";
import { clearSessionState } from "../../services/storage/session-state.storage";
import { markSessionActive, markSessionExpired } from "../../services/api/interceptors";
import { FileApi } from "../../services/api/file";
import { UserApi } from "src/services/api/user";
import CompanyApi from "src/services/api/company";

// Helper function to serialize error objects for Redux state
const serializeError = (error) => {
  if (!error) return null;
  
  return {
    message: error?.message || 'Unknown error',
    code: error?.code || null,
    response: error?.response ? {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data
    } : null
  };
};

export function* signIn({ payload, meta }) {
  try {
    const data = yield call(AuthApi.signIn, payload);

    // MFA required — the server returned a pre-auth token instead of full JWT.
    if (data.mfa_required) {
      yield call(AuthStorage.storeMfaPreAuthToken, data.pre_auth_token);
      yield call(AuthStorage.storeMfaEnrolled, data.mfa_enrolled);
      yield put(mfaPending({ preAuthToken: data.pre_auth_token, mfaEnrolled: data.mfa_enrolled }));
      yield call(() => {
        window.location.href = data.mfa_enrolled ? '/mfa-verify' : '/mfa-setup';
      });
      return;
    }

    yield call(AuthStorage.storeAccessToken, data.access);
    yield call(AuthStorage.storeRefreshToken, data.refresh);
    // Re-arm the refresh machinery, which latches off after a failed refresh.
    yield call(markSessionActive);

    // Branch on onboarding-status BEFORE calling /profile/, because /profile/
    // returns 403 for users without a CompanyUserRole (e.g. an admin who has just
    // self-registered and not yet created their company).
    let onboarding;
    try {
      onboarding = yield call(AuthApi.getOnboardingStatus);
    } catch (statusError) {
      // If the onboarding-status call itself fails (network/5xx), abort the login
      // cleanly so the user isn't dropped into a half-authenticated UI.
      yield call(AuthStorage.removeAccessToken);
      yield call(AuthStorage.removeRefreshToken);
      yield call(AuthStorage.removeUser);
      const serializableError = serializeError(statusError);
      yield put(signInError(serializableError));
      if (meta?.onError) {
        meta.onError(statusError?.response?.data || serializableError);
      }
      return;
    }

    if (onboarding?.status === "needs_company") {
      // Authenticated, but no company yet -> route to standalone onboarding page.
      // Store a minimal user with role=null so the rest of the app can recognize the state,
      // but DO NOT call /profile/ (it would 403) and DO NOT show the main UI.
      const onboardingUser = {
        email: onboarding.email || payload.email,
        first_name: onboarding.first_name || "",
        last_name: onboarding.last_name || "",
        id: "pending-onboarding",
        role: null,
        company: null,
        requires_onboarding: true,
      };
      yield put(signInSuccess(onboardingUser));
      yield call(AuthStorage.storeUser, onboardingUser);
      // Bypass the sign-in component's onSuccess (which redirects to /upload)
      // and force-navigate to the onboarding page instead.
      yield call(() => {
        window.location.href = "/onboarding/create-company";
      });
      return;
    }

    if (onboarding?.status === "pending_company_approval") {
      // Company created but awaiting staff approval. Don't call /profile/ (it 403s
      // while pending); route to the standalone pending-approval page.
      const pendingUser = {
        email: onboarding.email || payload.email,
        first_name: onboarding.first_name || "",
        last_name: onboarding.last_name || "",
        id: "pending-approval",
        role: "admin",
        company: null,
        requires_approval: true,
      };
      yield put(signInSuccess(pendingUser));
      yield call(AuthStorage.storeUser, pendingUser);
      yield call(() => {
        window.location.href = "/onboarding/pending-approval";
      });
      return;
    }

    // status === "ready": user has a CompanyUserRole, fetch the full profile.
    let authData;
    try {
      authData = yield call(AuthApi.getAuthUser);
    } catch (profileError) {
      // /profile failure for a "ready" user is unexpected; treat as a login failure
      // rather than leaving the session half-authenticated.
      yield call(AuthStorage.removeAccessToken);
      yield call(AuthStorage.removeRefreshToken);
      yield call(AuthStorage.removeUser);
      const serializableError = serializeError(profileError);
      yield put(signInError(serializableError));
      if (meta?.onError) {
        meta.onError(profileError?.response?.data || serializableError);
      }
      return;
    }
    yield put(signInSuccess(authData));
    yield call(AuthStorage.storeUser, authData);

    if (meta?.onSuccess) {
      yield call(meta.onSuccess, authData);
    }
    yield call(FileApi.prefetchGhsCatalog);
  } catch (error) {
    const serializableError = serializeError(error);
    yield put(signInError(serializableError));
    if (meta?.onError) {
      meta.onError(error?.response?.data || error?.data || serializableError);
    }
  }
}

export function* logout() {
  try {
    // Latch first: from here on the session is gone, so any request that 401s
    // mid-logout must not try to refresh it.
    yield call(markSessionExpired);
    // Blacklist the refresh token server-side before clearing local state.
    // Ignore errors so the local logout always completes even if the server is unreachable.
    try {
      yield call(AuthApi.logout);
    } catch (_) {
      // Server logout failed (e.g. network error, token already expired) — proceed anyway.
    }
    yield put(logoutSuccess());
    yield call(AuthStorage.removeUser);
    yield call(AuthStorage.removeAccessToken);
    yield call(AuthStorage.removeRefreshToken);
    yield call(AuthStorage.removeMfaPreAuthToken);
    yield call(AuthStorage.removeMfaEnrolled);
    // Table lock/validating flags and row-ID sets are scoped to the session that
    // wrote them. Left behind they follow the next login on the same browser —
    // most visibly as a table that stays read-only.
    yield call(clearSessionState);
    yield call(FileApi.clearGhsCatalogCache);
    // Navigate to login page instead of reloading
    yield call(() => {
      window.location.href = "/login";
    });
  } catch (error) {
    yield put(logoutError(serializeError(error)));
  }
}

export function* getCounts() {
  try {
    const res = yield call(FileApi.getFilesCount);
    yield put(getCountsSuccess(res));
  } catch (error) {
    yield put(getCountsError(serializeError(error)));
  }
}

export function* getHp() {
  try {
    const res = yield call(FileApi.getHazardPoints);
    const result = [];
    const uniqueItems = new Set();

    for (const item of res) {
      // Create a unique key using both h_punto and categoria_di_pericolo
      const uniqueKey = item.categoria_di_pericolo
        ? `${item.h_punto}_${item.categoria_di_pericolo}`
        : item.h_punto;

      // Skip if we've already processed this unique combination
      if (uniqueItems.has(uniqueKey)) continue;
      uniqueItems.add(uniqueKey);

      if (item.categoria_di_pericolo) {
        result.push({
          ...item,
          h_punto: `${item.h_punto} ${item.categoria_di_pericolo}`,
        });
        continue;
      }

      result.push(item);
    }

    yield put(getHpSuccess(result));
  } catch (error) {
    yield put(getHpError(serializeError(error)));
  }
}

export function* getProfile() {
  try {
    const profile = yield call(UserApi.getUserProfile);
    yield put(profileData(profile));
  } catch (error) {
    yield put(profileData(null));
  }
}

export function* getCompany() {
  try {
    const company = yield call(CompanyApi.getCompanyDetail);
    yield put(companyData(company));
  } catch (error) {
    yield put(companyData(null));
  }
}
